import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config';

function ResetPassword() {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [status, setStatus] = useState('idle'); // idle, verifying, success, error
  const [message, setMessage] = useState('');

  // Resend OTP Cooldown
  const [cooldown, setCooldown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const inputsRef = useRef([]);

  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_\-+=~`\[\]\\;'/]).{8,}$/;

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1]);
    const urlEmail = params.get('email');
    if (urlEmail) setEmail(urlEmail);
    else {
      setStatus('error');
      setMessage('Missing email address. Please request a new password reset from the login screen.');
    }
  }, []);

  useEffect(() => {
    let timer;
    if (cooldown > 0 && !canResend) {
      timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    } else if (cooldown === 0) {
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [cooldown, canResend]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-advance
    if (value && index < 5) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).replace(/\D/g, '');
    if (pastedData) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      const focusIndex = pastedData.length < 6 ? pastedData.length : 5;
      inputsRef.current[focusIndex].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = otp.join('');
    if (token.length !== 6) {
      setStatus('error');
      setMessage('Please enter all 6 digits of the verification code.');
      return;
    }

    if (password !== confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match.');
      return;
    }
    if (!passwordRegex.test(password)) {
      setStatus('error');
      setMessage('Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character.');
      return;
    }

    setStatus('verifying');
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, password })
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message);
      } else {
        setStatus('error');
        setMessage(data.message || 'Password reset failed.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Network error during password reset.');
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setCanResend(false);
    setCooldown(60);
    setStatus('idle');
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('A new code has been sent to your email.');
      } else {
        setMessage(data.message || 'Failed to resend code.');
      }
    } catch (err) {
      setMessage('Network error resending code.');
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--bg-dark, #0d1117)', color: '#c9d1d9' }}>
      <div style={{ background: '#161b22', padding: '40px', borderRadius: '10px', textAlign: 'center', border: '1px solid #30363d', maxWidth: '450px', width: '100%' }}>
        <h2>Reset Password</h2>
        <p style={{ color: '#8b949e', marginBottom: '20px', fontSize: '14px' }}>
          Enter the 6-digit code sent to <br/><strong>{email}</strong>
        </p>

        {status === 'success' ? (
          <>
            <p style={{ color: '#3fb950', marginTop: '15px' }}>{message}</p>
            <button 
              onClick={() => window.location.href = '#/'} 
              style={{ marginTop: '20px', padding: '10px 20px', background: '#238636', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
            >
              Go to Login
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '10px' }} onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  style={{
                    width: '40px',
                    height: '50px',
                    fontSize: '24px',
                    textAlign: 'center',
                    borderRadius: '5px',
                    border: '1px solid #30363d',
                    background: '#0d1117',
                    color: '#fff',
                  }}
                />
              ))}
            </div>

            <input
              type="password"
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ padding: '10px', borderRadius: '5px', border: '1px solid #30363d', background: '#0d1117', color: '#fff' }}
            />
            <input
              type="password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              style={{ padding: '10px', borderRadius: '5px', border: '1px solid #30363d', background: '#0d1117', color: '#fff' }}
            />

            {status === 'error' && <div style={{ color: '#f85149', fontSize: '14px', textAlign: 'left' }}>{message}</div>}
            {status === 'idle' && message && <div style={{ color: '#3fb950', fontSize: '14px', textAlign: 'left' }}>{message}</div>}

            <button 
              type="submit" 
              disabled={status === 'verifying'}
              style={{ padding: '12px', background: '#238636', color: '#fff', border: 'none', borderRadius: '5px', cursor: status === 'verifying' ? 'not-allowed' : 'pointer', marginTop: '10px', fontWeight: 'bold' }}
            >
              {status === 'verifying' ? 'Verifying...' : 'Reset Password'}
            </button>

            <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
              <a href="#/" style={{ color: '#58a6ff', textDecoration: 'none' }}>Back to Login</a>
              <button 
                type="button" 
                onClick={handleResend}
                disabled={!canResend}
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: canResend ? '#58a6ff' : '#8b949e', 
                  cursor: canResend ? 'pointer' : 'not-allowed',
                  textDecoration: canResend ? 'underline' : 'none'
                }}
              >
                {canResend ? 'Resend Code' : `Resend in ${cooldown}s`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
